import { Chip } from '@mui/material';
import { Stack } from '@mui/system';
import {
  GridRenderCellParams,
  GridSingleSelectColDef,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid';
import { ReactNode } from 'react';

import { SingleOperators } from 'common/src/query-params/operators';

import { singleSelectFilter } from '../Filters/CustomCollectors';
import InfoTooltip from '../InfoTooltip';

interface BooleanColumnProps {
  /** Logic for displaying 'Yes' (and coincidentally 'No') values. */
  displayLogic?: { valueForYes: boolean; yesIsGreen: boolean };
  /**
   * Custom labels to replace 'Yes' and 'No'. If a component is provided,
   * it will be rendered as-is without a tooltip.
   * */
  labels?: { yesLabel: string | ReactNode; noLabel: string | ReactNode };
  /** Specifies whether filtering is handled client-side or server-side. */
  filterMode?: 'client' | 'server';
  /** Optional additional server-side filters for a selected value. */
  serverAdditionalFilters?: (
    value: boolean,
  ) => Array<{ field: string; operator: SingleOperators; value: unknown }>;
  /** Whether to replace the column's value filter when providing server filters. */
  replaceBaseFilter?: (value: boolean) => boolean;
  /** Whether to include an informational tooltip. */
  includeTooltip?: boolean;
  /** Optional text to override the default tooltip text. */
  tooltipText?: string;
  /** Optional width of the column. */
  width?: number;
  /** Whether to disable the filter input component. */
  disableFilterInput?: boolean;
}

/** A helper to generate some params for a boolean MUI column. */
const addBooleanColumnProps = ({
  displayLogic = { valueForYes: true, yesIsGreen: true },
  labels = { yesLabel: 'Yes', noLabel: 'No' },
  filterMode = 'client',
  serverAdditionalFilters,
  replaceBaseFilter,
  includeTooltip = false,
  tooltipText = 'This item does not show up in any reports',
  width = 130,
  disableFilterInput = false,
}: BooleanColumnProps = {}): Partial<GridSingleSelectColDef> => {
  const { yesLabel, noLabel } = labels;
  const { valueForYes, yesIsGreen } = displayLogic;
  const clientFilterOperators = getGridSingleSelectOperators().map(
    (operator) => ({
      ...operator,
      InputComponentProps: {
        ...(operator.InputComponentProps || {}),
        fullWidth: true,
        sx: {
          width: 190,
        },
      },
    }),
  );
  const valueOptions = [
    {
      label: valueForYes ? yesLabel : noLabel,
      value: true,
      additionalFilters: serverAdditionalFilters?.(true),
      replaceBaseFilter: replaceBaseFilter?.(true),
    },
    {
      label: valueForYes ? noLabel : yesLabel,
      value: false,
      additionalFilters: serverAdditionalFilters?.(false),
      replaceBaseFilter: replaceBaseFilter?.(false),
    },
  ];

  const filterRelatedValues =
    filterMode === 'server'
      ? {
          filterOperators: singleSelectFilter(
            {
              service: {
                data: [
                  { id: 1, ...valueOptions[0] },
                  { id: 2, ...valueOptions[1] },
                ],
                fieldset: { key: 'id', label: 'label', value: 'value' },
              },
              disabled: disableFilterInput,
            },
            [
              SingleOperators.GREATER_THAN,
              SingleOperators.GREATER_THAN_OR_EQUAL_TO,
              SingleOperators.LESS_THAN,
              SingleOperators.LESS_THAN_OR_EQUAL_TO,
              SingleOperators.NOT_EQUAL_TO,
            ],
          ),
        }
      : {
          valueOptions,
          filterOperators: clientFilterOperators,
        };

  const BooleanColumnCell = ({ value }: GridRenderCellParams): ReactNode => {
    const invertedValue = valueForYes ? value : !value;
    const label = invertedValue ? yesLabel : noLabel;
    const isActuallyDeactivated = !value;
    const shouldShowTooltip = includeTooltip && isActuallyDeactivated;
    const isComponentLabel = typeof label !== 'string';
    let color: 'success' | 'error';

    if (isComponentLabel) {
      return label;
    }

    if (yesIsGreen) {
      color = label === yesLabel ? 'success' : 'error';
    } else {
      color = label === yesLabel ? 'error' : 'success';
    }

    return (
      <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
        {label && <Chip variant="outlined" label={label} color={color} />}
        {shouldShowTooltip && <InfoTooltip infoMessage={tooltipText} />}
      </Stack>
    );
  };

  return {
    type: 'singleSelect',
    headerAlign: 'center',
    width,
    display: 'flex', // Needed to allow align prop below to work.
    align: 'center',
    ...filterRelatedValues,
    renderCell: (params: GridRenderCellParams) => (
      <BooleanColumnCell {...params} />
    ),
  };
};

export default addBooleanColumnProps;
